import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, TrendingUp, Users, ArrowUpRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminStatsPage = () => {
  const [data, setData] = useState(null);
  const [secLogs, setSecLogs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
      axios.get(`${API_URL}/api/admin/security-logs?limit=20`, { withCredentials: true }),
    ])
      .then(([statsRes, logsRes]) => {
        setData(statsRes.data);
        setSecLogs(logsRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-red-600" /></div>;
  if (!data) return <p className="text-center text-slate-500 py-20">Erreur</p>;

  return (
    <div className="space-y-6" data-testid="admin-stats">
      <h1 className="text-2xl font-bold text-slate-900">Statistiques</h1>

      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">MRR</p>
                <p className="text-3xl font-bold text-slate-900">{data.mrr.toFixed(0)}€</p>
                <p className="text-xs text-slate-400 mt-1">{data.total_paid} marchands payants</p>
              </div>
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Nouveaux cette semaine</p>
                <p className="text-3xl font-bold text-slate-900">{data.new_merchants_this_week}</p>
              </div>
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Conversion trial &rarr; payant</p>
                <p className="text-3xl font-bold text-slate-900">{data.conversion_rate}%</p>
                <p className="text-xs text-slate-400 mt-1">{data.total_paid} / {data.total_merchants}</p>
              </div>
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                <ArrowUpRight className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top 10 */}
      <Card className="border-slate-200">
        <CardHeader><CardTitle>Top 10 marchands par activité (scans)</CardTitle></CardHeader>
        <CardContent>
          {data.top_merchants.length === 0 ? (
            <p className="text-sm text-slate-500">Aucune donnée</p>
          ) : (
            <div className="space-y-2">
              {data.top_merchants.map((m, i) => (
                <div key={m.merchant_id} className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    i < 3 ? 'bg-red-600 text-white' : 'bg-slate-200 text-slate-600'
                  }`}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{m.name}</p>
                    <p className="text-xs text-slate-500">{m.email}</p>
                  </div>
                  <span className="font-bold text-slate-900">{m.scans} scans</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security Logs */}
      <Card className="border-slate-200" data-testid="admin-security-logs">
        <CardHeader><CardTitle>Logs de sécurité</CardTitle></CardHeader>
        <CardContent>
          {!secLogs || secLogs.logs.length === 0 ? (
            <p className="text-sm text-slate-500">Aucun événement de sécurité</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Date</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">IP</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Code membre</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Raison</th>
                  </tr>
                </thead>
                <tbody>
                  {secLogs.logs.map((log, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-2 px-3 text-xs text-slate-500">{log.timestamp?.slice(0, 19).replace('T', ' ')}</td>
                      <td className="py-2 px-3 font-mono text-xs">{log.ip}</td>
                      <td className="py-2 px-3 font-mono text-xs">{log.member_code}</td>
                      <td className="py-2 px-3">
                        <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">{log.reason}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-slate-400 mt-2">{secLogs.total} événements au total</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminStatsPage;
